import { Link as RouterLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloudOffOutlined from '@mui/icons-material/CloudOffOutlined';
import { adminBusinessQueryKey, fetchAdminBusinessJson } from '@/lib/queryClient';

/**
 * When GET `/api/admin/business` returns 404, only `/admin/settings` is reachable; other admin routes redirect here.
 */
export default function AdminBusinessGate() {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: business, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: adminBusinessQueryKey,
    queryFn: fetchAdminBusinessJson,
  });

  if (isPending) {
    return (
      <Box sx={{ flex: 1, p: 2, minHeight: 200 }}>
        <Skeleton variant="rounded" height={32} width={240} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height="100%" sx={{ minHeight: 320 }} />
      </Box>
    );
  }

  if (isError) {
    const detail = error instanceof Error ? error.message : String(error);
    return (
      <Box
        role="status"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 320, sm: 420 },
          px: 2,
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 440,
            width: '100%',
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={2} alignItems="center">
            <CloudOffOutlined sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.9 }} aria-hidden />
            <Typography variant="h6" component="h1" fontWeight={600}>
              {t('adminBusinessGate.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {t('adminBusinessGate.description')}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontFamily: 'ui-monospace, monospace', wordBreak: 'break-word' }}>
              {detail}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1, width: '100%' }} justifyContent="center">
              <Button variant="contained" onClick={() => refetch()} disabled={isFetching} fullWidth sx={{ maxWidth: 200 }}>
                {isFetching ? t('common.loading') : t('actions.retry')}
              </Button>
              <Button component={RouterLink} to="/admin/settings" variant="outlined" fullWidth sx={{ maxWidth: 200 }}>
                {t('nav.settings')}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5, lineHeight: 1.5 }}>
              {t('adminBusinessGate.settingsHint')}
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  const hasBusiness = business != null;
  const onSettings = location.pathname.startsWith('/admin/settings');

  if (!hasBusiness && !onSettings) {
    return <Navigate to="/admin/settings" replace />;
  }

  return <Outlet />;
}
